/** @jsx jsx */
// External libraries
import { jsx } from '@emotion/core';
import { useState } from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';

// Internal modules
import { CURRENT_YEAR } from './constants';
import { useMountLogging } from '@/lib/reactHooks';
import { LanguageList } from '../Locale';
import { StyledLink, StyledText } from '../Common';
import {
  EntityMessage,
  FooterLegalLinks,
  LazyCountryChangeInterstitial,
  PrivacyAndSecurityMessage,
} from './Components';
import {
  features,
  getContent,
  getGlobals,
  getLocale,
  getToken,
  postToMerchantForOldIncontext,
} from '@/lib';
import {
  ChangeCountry,
  CountryLanguageContainer,
  FooterLinks,
  HorizLine,
  LinksContainer,
  StyledFooter,
} from './StyledComponents';
import {
  $logger,
  INTERSTITIAL_PAYLOAD,
  STATE_NAME,
  TRANSITION_NAME,
} from '@/lib/logger';

const loadCountryChangeInterstitial = () => {
  LazyCountryChangeInterstitial.preload();
};

const Footer = props => {
  useMountLogging('Footer');
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const cancelUrl = useStoreState(({ api }) => api.cancelUrl);
  const intent = useStoreState(({ api }) => api.intent);
  const toggleIsPageLoading = useStoreActions(({ ui }) => {
    return ui.toggleIsPageLoading;
  });
  const territoriesByRegion = useStoreState(({ api }) => {
    const { locale } = getLocale();
    return api.griffinMetadata.territoriesByRegion[locale];
  });
  const [
    isCountryChangeInterstitialOpen,
    setIsCountryChangeInterstitialOpen,
  ] = useState(false);
  const { country, language } = getLocale();
  const { enabledLanguages } = features.config;

  const handleReturnLinkClick = e => {
    e.preventDefault();

    if (isFormSubmitting) {
      return;
    }

    toggleIsPageLoading(true);
    const { cancelAndRtm } = TRANSITION_NAME;

    $logger.fpti(`weasley_${cancelAndRtm}`, {
      state_name: STATE_NAME.backToMerchant,
      transition_name: cancelAndRtm,
    });
    $logger.flushThen(() => {
      if (window.xprops) {
        const token = getToken();
        const data = {
          cancelUrl,
          intent: intent?.toLowerCase(),
          orderID: token,
          paymentToken: token,
        };
        window.xprops.onCancel(data);
        return;
      }

      // for old checkout.js integration send post message
      const didPostToMerchantForOldIncontext = postToMerchantForOldIncontext(
        cancelUrl,
      );
      if (didPostToMerchantForOldIncontext) {
        return;
      }

      window.location.assign(cancelUrl);
    });
  };

  const toggleCountryChangeInterstitial = e => {
    // We need to wait until the territories metadata has been
    // fetched from the API before we open the interstitial,
    // otherwise we won't have the country list.
    if (
      !territoriesByRegion.AMERICAS.length ||
      isFormSubmitting ||
      features.isActive('blockNonDomesticPayer')
    ) {
      return;
    }

    const action = isCountryChangeInterstitialOpen ? 'close' : 'open';
    const payload = INTERSTITIAL_PAYLOAD({ action, name: 'country_change' });

    $logger.fpti(`weasley_${payload.transition_name}`, payload);
    setIsCountryChangeInterstitialOpen(!isCountryChangeInterstitialOpen);
  };

  return (
    <div>
      <StyledFooter>
        <HorizLine hasCancelUrl={Boolean(cancelUrl)} />

        {Boolean(cancelUrl) && (
          <StyledLink
            css={{ fontSize: '13px' }}
            data-test-id="footer-return-link"
            href="#"
            onClick={handleReturnLinkClick}
          >
            {getContent('billing.cancelAndRTM')}
          </StyledLink>
        )}

        <CountryLanguageContainer>
          {!getGlobals().isBasl && enabledLanguages.length && (
            <LanguageList currLanguage={language} />
          )}

          <ChangeCountry
            country={country}
            data-test-id="footer-change-country"
            onClick={toggleCountryChangeInterstitial}
            onFocus={loadCountryChangeInterstitial}
            onMouseOver={loadCountryChangeInterstitial}
          />
        </CountryLanguageContainer>

        <FooterLinks>
          <LinksContainer data-test-id="footer-links">
            <FooterLegalLinks />
          </LinksContainer>

          <StyledText css={{ fontSize: '11px' }}>
            {getContent('footer.copyright', { year: CURRENT_YEAR })}
          </StyledText>
        </FooterLinks>

        {features.isActive('hasFooterPrivacyAndSecurityMessage') && (
          <PrivacyAndSecurityMessage />
        )}

        {features.isActive('hasFooterEntityMessage') && <EntityMessage />}
      </StyledFooter>

      {isCountryChangeInterstitialOpen && (
        <LazyCountryChangeInterstitial
          handleClose={toggleCountryChangeInterstitial}
          territoriesByRegion={territoriesByRegion}
        />
      )}
    </div>
  );
};

export { Footer };
